import { type ComponentProps, type JSX, type ReactNode } from "react";
import { type DateRange } from "react-day-picker";
import { type MultiSelectCatFilterProps } from "packages/vega-core/components/MultiSelectFilter/MultiSelectCatFilter";

import { type ThemedButtonProps } from "@vega-core/components/Buttons/ThemedButton";
import { type ThemedIconButtonProps } from "@vega-core/components/Buttons/ThemedIconButton";
import { type ThemedDateRangeProps } from "@vega-core/components/Form/Controls/DateField/ThemedDateRange";
import {
  type NestedMultiselectDropdownProps,
  type NestedOptionType,
  type SelectedOptionType,
} from "@vega-core/components/Form/Controls/Dropdown/NestedMultiselectDropdown/types";
import { type ThemedDropdownProps } from "@vega-core/components/Form/Controls/Dropdown/ThemedDropdown/ThemedDropdown";
import { type ThemedMultiselectDropdownProps } from "@vega-core/components/Form/Controls/Dropdown/ThemedMultiselectDropdown/ThemedMultiselectDropdown";
import { type SearchInputProps } from "@vega-core/components/Form/Controls/Input/SearchInput/SearchInput";
import { type SearchableDropdown as SearchableDropdownComponent } from "@vega-core/components/Form/Controls/SearchableDropdown/SearchableDropdown";
import { type SwitchProps } from "@vega-core/components/Switch/Switch";
import { type OptionType } from "@vega-core/models/options";

export type FilterValue =
  | DateRange
  | NestedOptionType[]
  | OptionType
  | OptionType[]
  | string[]
  | boolean
  | string
  | undefined;

export enum FilterType {
  Search = "search",
  Dropdown = "dropdown",
  SearchableDropdown = "searchableDropdown",
  Multiselect = "multiselect",
  MultiSelectNested = "multiSelectNested",
  DateRange = "dateRange",
  Switch = "switch",
  MultiSelectNestedV2 = "multiSelectNestedV2",
}

export type BaseFilterConfig = {
  id: string;
  type: FilterType;
  label: string;
  hideFromModal?: boolean;
  isVisible?: boolean;
  defaultValue?: FilterValue;
  showLabel?: boolean;
};

export type SearchFilterConfig = BaseFilterConfig & {
  type: FilterType.Search;
  props?: Partial<Omit<SearchInputProps, "name" | "onChange" | "value">>;
};

export type DropdownFilterConfig = BaseFilterConfig & {
  type: FilterType.Dropdown;
  props: Partial<Omit<ThemedDropdownProps, "name" | "onChange" | "value">>;
};

export type SearchableDropdownFilterConfig = BaseFilterConfig & {
  type: FilterType.SearchableDropdown;
  props: Partial<
    Omit<
      ComponentProps<typeof SearchableDropdownComponent>,
      "name" | "onChange" | "value"
    >
  > & { options: OptionType[] };
};

export type MultiselectFilterConfig = BaseFilterConfig & {
  type: FilterType.Multiselect;
  props: Partial<
    Omit<
      ThemedMultiselectDropdownProps,
      "name" | "selectedOptions" | "setSelectedOptions"
    >
  > & { options: OptionType[] };
};
export type MultiSelectNestedFilterConfig = BaseFilterConfig & {
  type: FilterType.MultiSelectNested;
  props: Partial<
    Omit<
      NestedMultiselectDropdownProps,
      "selectedOptions" | "setSelectedOptions"
    >
  > & { options: NestedOptionType[] };
};

export type DateRangeFilterConfig = BaseFilterConfig & {
  type: FilterType.DateRange;
  props?: Partial<Omit<ThemedDateRangeProps, "name" | "onChange" | "value">>;
};

export type SwitchFilterConfig = BaseFilterConfig & {
  type: FilterType.Switch;
  props?: Partial<Omit<SwitchProps, "checked" | "onChange">>;
};

export type MultiSelectNestedV2FilterConfig = BaseFilterConfig & {
  type: FilterType.MultiSelectNestedV2;
  props: Partial<
    Omit<
      MultiSelectCatFilterProps,
      "selectedCategories" | "setSelectedCategories"
    >
  > & { categories: NestedOptionType[] };
};

export type FilterConfig =
  | DateRangeFilterConfig
  | DropdownFilterConfig
  | MultiselectFilterConfig
  | MultiSelectNestedFilterConfig
  | MultiSelectNestedV2FilterConfig
  | SearchableDropdownFilterConfig
  | SearchFilterConfig
  | SwitchFilterConfig;

export enum TableFilterId {
  LPOfferings = "lp-offerings",
  CapitalActivity = "capital-activity",
  CapitalActivityReporting = "capital-activity_reporting",
  Cases = "cases",
  CASManagement = "cas-management",
  Collaborators = "collaborators",
  CoInvestments = "co-investments",
  CoInvestmentsGP = "co-investments-gp",
  CoInvestmentsAudit = "co-investments-audit",
  EmailsList = "emails-list",
  NotificationHub = "notification-hub",
  FundCarriedInterest = "fund-carried-interest",
  FundInvestments = "fund-investments",
  FundOpportunities = "fund-opportunities",
  OpportunityDocuments = "opportunity-documents",
  FundSubscriptions = "fund-subscriptions",
  GaiaFunds = "gp-gaia-funds",
  GaiaPerformance = "gp-gaia-performance",
  GaiaValuation = "gp-gaia-valuation",
  GPOfferings = "gp-offerings",
  InvestmentCategoryActivity = "investment-category-activity",
  InvestmentCategorySummary = "investment-category-summary",
  InvestmentUserActivity = "investment-user-activity",
  InvestmentUserSummary = "investment-user-summary",
  IndividualInvestors = "individual-investors",
  OrganizationsInvestors = "organizations-investors",
  InvestorDocuments = "investor-documents",
  OfferingsDocuments = "offerings-documents",
  LPHome = "lp-home",
  OfferingCategoryActivity = "offering-category-activity",
  OfferingCategorySummary = "offering-category-summary",
  OfferingUserActivity = "offering-user-activity",
  OfferingUserSummary = "offering-user-summary",
  SubscriptionsTracker = "subscriptions-tracker",
  Tasks = "tasks",
  WorkbenchAnalytics = "workbench-analytics",
  WorkbenchAnalyticsTable = "workbench-analytics-table",
  InvestorPortalAnalytics = "investor-portal-analytics",
  UserLoginSessions = "user-login-sessions",
  FundMetrics = "fund-metrics",
  FundMetricsTimeFrame = "fund-metrics-timeframe",
  ConversionTab = "conversion-tab",
  EfficiencyTab = "efficiency-tab",
  IdManagerOrganizations = "id-manager-organizations",
  IdManagerUsers = "id-manager-users",
  IdManagerInvestingEntity = "id-manager-investing-entity",
  IdManagerInvestments = "id-manager-investments",
  IdManagerOpportunities = "id-manager-opportunities",
  IdManagerWireInstructions = "id-manager-wire-instructions",
  IdManagerLimitedPartners = "id-manager-limited-partners",
  IdManagerInvestmentMetrics = "id-manager-investment-metrics",
  IdManagerInvestorDocuments = "id-manager-investor-documents",
  IdManagerCapitalActivities = "id-manager-capital-activities",
  IdManagerFunds = "id-manager-funds",
  IdManagerUserRoles = "id-manager-user-roles",
  CoInvestmentsTracking = "co-investments-tracking",
  CoInvestmentsProcess = "co-investments-process",
  CoInvestmentsAnalytics = "co-investments-analytics",
  CoInvestmentsShareApproved = "co-investments-share-approved",
  CoInvestmentsSharePending = "co-investments-share-pending",
  OrganisationOpportunities = "organisation-opportunities",
  GaiaCharacteristics = "gp-gaia-characteristics",
}

export type TableFiltersConfig = {
  filters: readonly FilterConfig[];
  showResetButton?: boolean;
  showSaveFilters?: boolean;
  actions?: TableActionButtonConfig[];
  maxShownFilters?: number;
  noMarginBottom?: boolean;
  disabled?: boolean;
};

export type FilterState = Record<
  string,
  { value: FilterValuesByType[FilterType]; type: FilterType }
>;

export type FilterValuesByType = {
  [FilterType.DateRange]: DateRange;

  [FilterType.Dropdown]: OptionType | undefined;

  [FilterType.SearchableDropdown]: OptionType | undefined;

  [FilterType.Multiselect]: OptionType[];

  [FilterType.Search]: string;

  [FilterType.Switch]: boolean;

  [FilterType.MultiSelectNested]: SelectedOptionType[];

  [FilterType.MultiSelectNestedV2]: NestedOptionType[];
};

export type UseTableFiltersReturn = {
  filterState: FilterState;
  setFilterValue: (filterId: string, value: FilterValue) => void;
  resetFilters: () => void;
  hasActiveFilters: boolean;
  activeFilterCount: number;
  renderFilters: () => ReactNode;
};

export type UseTableFiltersOptions = {
  syncWithURL?: boolean;
  urlPrefix?: string;
};

export type SavedFiltersHook = {
  // Data
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  savedFilters: { id: string; filterName: string; filterConfig: any }[];
  hasFilters: boolean;

  // Actions
  saveFilter: (name: string, filterState: FilterState) => Promise<void>;
  deleteFilter: (filterId: string) => Promise<void>;
  loadFilter: (filterId: string) => FilterState | undefined;

  // UI State
  isSaving: boolean;
  isDeleting: boolean;

  // React Query integration
  invalidateQuery: () => void;
};

export enum TableActionType {
  Export = "export",
  Button = "button",
  IconButton = "iconButton",
  Custom = "custom",
}

export type TableActionButtonConfig =
  | {
      id: string;
      type: TableActionType.Export;
      props: ThemedButtonProps;
    }
  | {
      id: string;
      type: TableActionType.IconButton;
      props: ThemedIconButtonProps;
    }
  | { id: string; type: TableActionType.Button; props: ThemedButtonProps }
  | { id: string; type: TableActionType.Custom; component: JSX.Element };
